import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Tab, Nav, Badge, Card, Row, Col, Dropdown } from "react-bootstrap";
import { FiArrowLeft, FiMoreVertical } from "react-icons/fi";
import {
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFilePowerpoint,
  FaFileImage,
  FaFileArchive,
  FaFileAlt,
} from "react-icons/fa";
import InternalFooter from "../components/InternalFooter/InternalFooter";
import CommonTable from "../components/CommonTable/CommonTable";
import EmptyState from "../components/EmptyState/EmptyState";
import { getStaffById, deleteStaffAddress, deleteStaffContact, deleteStaffEmployment, deleteStaffDocument } from "../../services/StaffService";
import axiosInstance from "../../services/AxiosInstance";
import { getSavedViewMode, saveViewMode } from "../../utils/viewPersistence";
import { usePageToolbar } from "../layouts/nav/PageToolbarContext";
import { useCanDo } from "../../hooks/useCanDo";
import "../Documents/documentDrive.css";

const StaffDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [viewMode, setViewMode] = useState(() => getSavedViewMode('staffDocuments', 'table'));
  const canEditStaff = useCanDo('staff', 'create_edit');
  const canDeleteStaff = useCanDo('staff', 'delete');
  const staffMutateActions = (editClick, deleteClick) => {
    const actions = [];
    if (canEditStaff) {
      actions.push({ label: 'Edit', icon: 'fas fa-edit', onClick: editClick });
    }
    if (canDeleteStaff) {
      actions.push({ label: 'Delete', icon: 'fas fa-trash', className: 'text-danger', onClick: deleteClick });
    }
    return actions;
  };

  const notifySuccess = (msg) => toast.success(`✔️ ${msg}`, { position: 'top-right', autoClose: 3000 });
  const notifyError = (msg) => toast.error(`❌ ${msg}`, { position: 'top-right', autoClose: 5000 });

  useEffect(() => {
    fetchStaffData();
  }, [id]);

  const fetchStaffData = async () => {
    try {
      setLoading(true);
      const res = await getStaffById(id);
      setStaff(res.data.data || res.data);
    } catch (error) {
      console.error("Staff fetch error:", error);
      notifyError(error?.response?.data?.message || "Failed to load staff data");
    } finally {
      setLoading(false);
    }
  };

  // Delete handlers
  const handleAddressDelete = async (addressId) => {
    if (!window.confirm("Are you sure you want to delete this address?")) return;
    try {
      await deleteStaffAddress(id, addressId);
      notifySuccess("Address deleted successfully");
      fetchStaffData();
    } catch (error) {
      notifyError(error?.response?.data?.message || "Failed to delete address");
    }
  };

  const handleContactDelete = async (contactId) => {
    if (!window.confirm("Are you sure you want to delete this contact?")) return;
    try {
      await deleteStaffContact(id, contactId);
      notifySuccess("Contact deleted successfully");
      fetchStaffData();
    } catch (error) {
      notifyError(error?.response?.data?.message || "Failed to delete contact");
    }
  };

  const handleEmploymentDelete = async (employmentId) => {
    if (!window.confirm("Are you sure you want to delete this employment record?")) return;
    try {
      await deleteStaffEmployment(id, employmentId);
      notifySuccess("Employment record deleted successfully");
      fetchStaffData();
    } catch (error) {
      notifyError(error?.response?.data?.message || "Failed to delete employment record");
    }
  };

  const handleDocumentDelete = async (documentId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) return;
    try {
      await deleteStaffDocument(id, documentId);
      notifySuccess("Document deleted successfully");
      fetchStaffData();
    } catch (error) {
      notifyError(error?.response?.data?.message || "Failed to delete document");
    }
  };

  // View mode handler
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    saveViewMode('staffDocuments', mode);
  };

  // File icon helper functions
  const getFileIcon = (file) => {
    if (!file || !file.file_type) return 'file';
    const fileType = file.file_type.toLowerCase();
    if (fileType.includes('pdf')) return 'pdf';
    if (fileType.includes('word') || fileType.includes('doc')) return 'word';
    if (fileType.includes('excel') || fileType.includes('sheet')) return 'excel';
    if (fileType.includes('powerpoint') || fileType.includes('presentation')) return 'powerpoint';
    if (fileType.includes('image') || fileType.includes('jpeg') || fileType.includes('jpg') || fileType.includes('png') || fileType.includes('gif')) return 'image';
    if (fileType.includes('zip') || fileType.includes('rar') || fileType.includes('archive')) return 'archive';
    return 'file';
  };

  const getFileIconColor = (fileType) => {
    const colorMap = {
      pdf: '#d93025',
      word: '#2b579a',
      excel: '#1d6f42',
      powerpoint: '#d04423',
      image: '#34a853',
      archive: '#ea4335',
      file: '#667eea'
    };
    return colorMap[fileType] || '#667eea';
  };

  const getFileIconComponent = (file, size = 48) => {
    const iconType = getFileIcon(file);
    const color = getFileIconColor(iconType);
    
    switch (iconType) {
      case 'pdf':
        return <FaFilePdf size={size} style={{ color }} />;
      case 'word':
        return <FaFileWord size={size} style={{ color }} />;
      case 'excel':
        return <FaFileExcel size={size} style={{ color }} />;
      case 'powerpoint':
        return <FaFilePowerpoint size={size} style={{ color }} />;
      case 'image':
        return <FaFileImage size={size} style={{ color }} />;
      case 'archive':
        return <FaFileArchive size={size} style={{ color }} />;
      default:
        return <FaFileAlt size={size} style={{ color }} />;
    }
  };

  const handleDocumentDownload = async (doc) => {
    if (!doc) {
      notifyError('Invalid document');
      return;
    }

    try {
      setLoading(true);
      
      // Always use staff document endpoint for staff documents
      const downloadUrl = `/staff/${id}/documents/${doc.id}/download`;

      const response = await axiosInstance.get(downloadUrl, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/octet-stream'
        }
      });

      if (response.status === 200) {
        // Get filename from Content-Disposition header or use document title/file name
        let fileName = doc.document_title || doc.file_name || 'document';
        
        // Try to extract filename from Content-Disposition header
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = decodeURIComponent(fileNameMatch[1].replace(/['"]/g, ''));
          }
        }

        // Create blob URL and trigger download
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        notifySuccess('File downloaded successfully');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      
      if (error.response && error.response.data instanceof Blob) {
        try {
          const errorText = await error.response.data.text();
          const errorJson = JSON.parse(errorText);
          notifyError(errorJson.message || 'Failed to download file');
        } catch (parseError) {
          notifyError('Failed to download file');
        }
      } else {
        const errorMessage = error.response?.data?.message ||
          error.response?.data?.error ||
          'Failed to download file';
        notifyError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const staffDetailToolbar = useMemo(() => (
    <>
      <div className="page-toolbar-bar__start">
        <button
          type="button"
          className="page-toolbar-back-btn"
          onClick={() => navigate("/staff/staffList")}
          title="Back"
          aria-label="Back"
        >
          <FiArrowLeft />
          Back
        </button>
      </div>
      {staff && canEditStaff ? (
        <div className="page-toolbar-bar__end">
          <button
            type="button"
            className="header-toolbar-btn seg-btn--wide"
            onClick={() => navigate(`/staff/editStaff/${id}`)}
            title="Edit Staff"
            aria-label="Edit Staff"
          >
            <i className="fa fa-edit" aria-hidden />
            <span>Edit Staff</span>
          </button>
        </div>
      ) : null}
    </>
  ), [staff, id, navigate, canEditStaff]);

  usePageToolbar(staffDetailToolbar, [staffDetailToolbar]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!staff) {
    return (
      <div className="text-center p-4">
        <p>Staff not found</p>
        <button className="btn user-form-btn sw-btn-next" onClick={() => navigate("/staff/staffList")}>Back to List</button>
      </div>
    );
  }

  const staffName = [staff.first_name, staff.middle_name, staff.last_name].filter(Boolean).join(" ");

  return (
    <div>
      <ToastContainer />

      <Card className="mt-3">
        <Card.Header>
          <div
            className="d-flex flex-row justify-content-between align-items-center gap-2"
            style={{ width: '100%', paddingLeft: '15px', paddingRight: '15px' }}
          >
            <h4
              className="mb-0 text-truncate"
              style={{ fontSize: '1.1rem', wordBreak: 'break-word', maxWidth: '100%' }}
            >
              {staffName}
              {staff.employee_code && (
                <span className="d-none d-md-inline"> - {staff.employee_code}</span>
              )}
            </h4>
          </div>
        </Card.Header>
        <Card.Body>
          <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
            {/* Mobile: Horizontal Scrollable Tabs */}
            <div className="d-md-none mb-3" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', msOverflowStyle: '-ms-autohiding-scrollbar' }}>
              <Nav as="ul" className="nav nav-pills" style={{ flexWrap: 'nowrap', minWidth: 'max-content', gap: '0.5rem' }}>
                <Nav.Item as="li" style={{ flexShrink: 0 }}>
                  <Nav.Link 
                    eventKey="details" 
                    className={activeTab === 'details' ? 'active' : ''}
                    style={{ whiteSpace: 'nowrap', padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                  >
                    Details
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item as="li" style={{ flexShrink: 0 }}>
                  <Nav.Link 
                    eventKey="addresses" 
                    className={activeTab === 'addresses' ? 'active' : ''}
                    style={{ whiteSpace: 'nowrap', padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                  >
                    Addresses
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item as="li" style={{ flexShrink: 0 }}>
                  <Nav.Link 
                    eventKey="contacts" 
                    className={activeTab === 'contacts' ? 'active' : ''}
                    style={{ whiteSpace: 'nowrap', padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                  >
                    Contacts
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item as="li" style={{ flexShrink: 0 }}>
                  <Nav.Link 
                    eventKey="employments" 
                    className={activeTab === 'employments' ? 'active' : ''}
                    style={{ whiteSpace: 'nowrap', padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                  >
                    Employments
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item as="li" style={{ flexShrink: 0 }}>
                  <Nav.Link 
                    eventKey="documents" 
                    className={activeTab === 'documents' ? 'active' : ''}
                    style={{ whiteSpace: 'nowrap', padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                  >
                    Documents
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </div>
            
            <Nav as="ul" className="nav-tabs d-none d-md-flex">
              <Nav.Item as="li">
                <Nav.Link eventKey="details">Details</Nav.Link>
              </Nav.Item>
              <Nav.Item as="li">
                <Nav.Link eventKey="addresses">Addresses</Nav.Link>
              </Nav.Item>
              <Nav.Item as="li">
                <Nav.Link eventKey="contacts">Contacts</Nav.Link>
              </Nav.Item>
              <Nav.Item as="li">
                <Nav.Link eventKey="employments">Employments</Nav.Link>
              </Nav.Item>
              <Nav.Item as="li">
                <Nav.Link eventKey="documents">Documents</Nav.Link>
              </Nav.Item>
            </Nav>

            <Tab.Content className="p-2 p-md-4">
              <Tab.Pane eventKey="details">
                {/* Mobile: single unified table for perfect alignment */}
                <Row className="d-md-none">
                  <Col xs={12}>
                    <div className="table-responsive">
                      <table className="table">
                        <tbody>
                          <tr><td><strong>Employee Code:</strong></td><td>{staff.employee_code}</td></tr>
                          <tr><td><strong>First Name:</strong></td><td>{staff.first_name}</td></tr>
                          <tr><td><strong>Middle Name:</strong></td><td>{staff.middle_name || "N/A"}</td></tr>
                          <tr><td><strong>Last Name:</strong></td><td>{staff.last_name || "N/A"}</td></tr>
                          <tr><td><strong>Gender:</strong></td><td>{staff.gender || "N/A"}</td></tr>
                          <tr><td><strong>Date of Birth:</strong></td><td>{staff.date_of_birth ? new Date(staff.date_of_birth).toLocaleDateString() : "N/A"}</td></tr>
                          <tr><td><strong>Blood Group:</strong></td><td>{staff.blood_group || "N/A"}</td></tr>
                          <tr><td><strong>Status:</strong></td><td><Badge bg={staff.status === 'Active' ? 'success' : staff.status === 'Inactive' ? 'secondary' : 'warning'}>{staff.status}</Badge></td></tr>
                          <tr><td><strong>Company:</strong></td><td>{staff.Corporate?.name || "N/A"}</td></tr>
                          <tr><td><strong>Branch:</strong></td><td>{staff.Branch?.name || "N/A"}</td></tr>
                          <tr><td><strong>Department:</strong></td><td>{staff.Department?.name || "N/A"}</td></tr>
                          <tr><td><strong>Sub Department:</strong></td><td>{staff.SubDepartment?.name || "N/A"}</td></tr>
                        </tbody>
                      </table>
                    </div>
                  </Col>
                </Row>

                {/* Desktop: two-column layout */}
                <Row className="d-none d-md-flex">
                  <Col md={6}>
                    <h5 className="d-none d-md-block">Personal Information</h5>
                    <div className="table-responsive">
                      <table className="table">
                        <tbody>
                          <tr><td><strong>Employee Code:</strong></td><td>{staff.employee_code}</td></tr>
                          <tr><td><strong>First Name:</strong></td><td>{staff.first_name}</td></tr>
                          <tr><td><strong>Middle Name:</strong></td><td>{staff.middle_name || "N/A"}</td></tr>
                          <tr><td><strong>Last Name:</strong></td><td>{staff.last_name || "N/A"}</td></tr>
                          <tr><td><strong>Gender:</strong></td><td>{staff.gender || "N/A"}</td></tr>
                          <tr><td><strong>Date of Birth:</strong></td><td>{staff.date_of_birth ? new Date(staff.date_of_birth).toLocaleDateString() : "N/A"}</td></tr>
                          <tr><td><strong>Blood Group:</strong></td><td>{staff.blood_group || "N/A"}</td></tr>
                          <tr><td><strong>Status:</strong></td><td><Badge bg={staff.status === 'Active' ? 'success' : staff.status === 'Inactive' ? 'secondary' : 'warning'}>{staff.status}</Badge></td></tr>
                        </tbody>
                      </table>
                    </div>
                  </Col>
                  <Col md={6}>
                    <h5 className="d-none d-md-block">Organizational Information</h5>
                    <div className="table-responsive">
                      <table className="table">
                        <tbody>
                          <tr><td><strong>Company:</strong></td><td>{staff.Corporate?.name || "N/A"}</td></tr>
                          <tr><td><strong>Branch:</strong></td><td>{staff.Branch?.name || "N/A"}</td></tr>
                          <tr><td><strong>Department:</strong></td><td>{staff.Department?.name || "N/A"}</td></tr>
                          <tr><td><strong>Sub Department:</strong></td><td>{staff.SubDepartment?.name || "N/A"}</td></tr>
                        </tbody>
                      </table>
                    </div>
                  </Col>
                </Row>
              </Tab.Pane>

              <Tab.Pane eventKey="addresses">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
                  <h5 className="mb-0 d-none d-md-block">Addresses</h5>
                  {canEditStaff ? (
                  <button 
                    className="btn user-form-btn sw-btn-next"
                    onClick={() => navigate(`/staff/${id}/address/add`)}
                    style={{ maxWidth: '150px', width: 'auto', flexShrink: 0, padding: '0.375rem 0.75rem', fontSize: '0.875rem' }}
                  >
                    Add Address
                  </button>
                  ) : null}
                </div>
                <CommonTable
                  columns={[
                    {
                      key: 'address_type',
                      header: 'Type',
                      accessor: (row) => row.address_type
                    },
                    {
                      key: 'address',
                      header: 'Address',
                      accessor: (row) => `${row.address_line_1 || ''} ${row.address_line_2 || ''}`.trim() || 'N/A'
                    },
                    {
                      key: 'city',
                      header: 'City',
                      accessor: (row) => row.city || 'N/A'
                    },
                    {
                      key: 'state',
                      header: 'State',
                      accessor: (row) => row.state || 'N/A'
                    },
                    {
                      key: 'country',
                      header: 'Country',
                      accessor: (row) => row.country || 'N/A'
                    },
                    {
                      key: 'current',
                      header: 'Current',
                      accessor: (row) => row.is_current ? <Badge bg="success">Current</Badge> : ''
                    }
                  ]}
                  data={staff.StaffAddresses || []}
                  actions={(row) => staffMutateActions(
                    () => navigate(`/staff/${id}/address/${row.id}/edit`),
                    () => handleAddressDelete(row.id)
                  )}
                  emptyMessage="No addresses found"
                  emptyStateIcon="fas fa-map-marker-alt"
                  getRowId={(row) => row.id}
                />
              </Tab.Pane>

              <Tab.Pane eventKey="contacts">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
                  <h5 className="mb-0 d-none d-md-block">Contacts</h5>
                  {canEditStaff ? (
                  <button 
                    className="btn user-form-btn sw-btn-next"
                    onClick={() => navigate(`/staff/${id}/contact/add`)}
                    style={{ maxWidth: '150px', width: 'auto', flexShrink: 0, padding: '0.375rem 0.75rem', fontSize: '0.875rem' }}
                  >
                    Add Contact
                  </button>
                  ) : null}
                </div>
                <CommonTable
                  columns={[
                    {
                      key: 'contact_type',
                      header: 'Type',
                      accessor: (row) => row.contact_type
                    },
                    {
                      key: 'email',
                      header: 'Email',
                      accessor: (row) => row.email || 'N/A'
                    },
                    {
                      key: 'phone_number',
                      header: 'Phone',
                      accessor: (row) => row.phone_number || 'N/A'
                    },
                    {
                      key: 'relation',
                      header: 'Relation',
                      accessor: (row) => row.relation || 'N/A'
                    }
                  ]}
                  data={staff.StaffContacts || []}
                  actions={(row) => staffMutateActions(
                    () => navigate(`/staff/${id}/contact/${row.id}/edit`),
                    () => handleContactDelete(row.id)
                  )}
                  emptyMessage="No contacts found"
                  emptyStateIcon="fas fa-phone"
                  getRowId={(row) => row.id}
                />
              </Tab.Pane>

              <Tab.Pane eventKey="employments">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
                  <h5 className="mb-0 d-none d-md-block">Employment Records</h5>
                  {canEditStaff ? (
                  <button 
                    className="btn user-form-btn sw-btn-next"
                    onClick={() => navigate(`/staff/${id}/employment/add`)}
                    style={{ maxWidth: '150px', width: 'auto', flexShrink: 0, padding: '0.375rem 0.75rem', fontSize: '0.875rem' }}
                  >
                    Add Employment
                  </button>
                  ) : null}
                </div>
                <CommonTable
                  columns={[
                    {
                      key: 'employment_type',
                      header: 'Type',
                      accessor: (row) => row.employment_type
                    },
                    {
                      key: 'joining_date',
                      header: 'Joining Date',
                      accessor: (row) => row.joining_date ? new Date(row.joining_date).toLocaleDateString() : 'N/A'
                    },
                    {
                      key: 'contract_renewal_date',
                      header: 'Contract Renewal Date',
                      accessor: (row) => row.contract_renewal_date ? new Date(row.contract_renewal_date).toLocaleDateString() : 'N/A'
                    },
                    {
                      key: 'last_renewed_date',
                      header: 'Last Renewed Date',
                      accessor: (row) => row.last_renewed_date ? new Date(row.last_renewed_date).toLocaleDateString() : 'N/A'
                    }
                  ]}
                  data={staff.StaffEmployments || []}
                  actions={(row) => staffMutateActions(
                    () => navigate(`/staff/${id}/employment/${row.id}/edit`),
                    () => handleEmploymentDelete(row.id)
                  )}
                  emptyMessage="No employment records found"
                  emptyStateIcon="fas fa-briefcase"
                  getRowId={(row) => row.id}
                />
              </Tab.Pane>

              <Tab.Pane eventKey="documents">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
                  <h5 className="mb-0 d-none d-md-block">Documents</h5>
                  <div className="d-flex align-items-center gap-2" style={{ flexWrap: 'wrap', width: '100%', justifyContent: 'flex-end' }}>
                    {/* View Toggle */}
                    <div className="view-toggle-container-modern staff-vehicle-view-toggle" style={{ flexShrink: 0, minWidth: '90px', width: 'auto' }}>
                      <button
                        className={`view-toggle-btn-modern ${viewMode === 'tile' ? 'active' : ''}`}
                        onClick={() => handleViewModeChange('tile')}
                        title="Card View"
                      >
                        <i className="fas fa-th-large"></i>
                      </button>
                      <button
                        className={`view-toggle-btn-modern ${viewMode === 'table' ? 'active' : ''}`}
                        onClick={() => handleViewModeChange('table')}
                        title="Table View"
                      >
                        <i className="fas fa-list"></i>
                      </button>
                    </div>
                    {canEditStaff ? (
                    <button 
                      className="btn user-form-btn sw-btn-next"
                      onClick={() => navigate(`/staff/${id}/document/add`)}
                      style={{ 
                        height: '38px', 
                        padding: '0.375rem 0.75rem', 
                        fontSize: '0.875rem', 
                        flexShrink: 0,
                        lineHeight: '1.2',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 'auto',
                        width: 'auto'
                      }}
                    >
                      Add Document
                    </button>
                    ) : null}
                  </div>
                </div>
                {viewMode === 'table' ? (
                  <CommonTable
                  columns={[
                    {
                      key: 'document_title',
                      header: 'Title',
                      accessor: (row) => row.document_title || 'N/A'
                    },
                    {
                      key: 'issued_by',
                      header: 'Issued By',
                      accessor: (row) => row.issued_by || 'N/A'
                    },
                    {
                      key: 'issued_date',
                      header: 'Issued Date',
                      accessor: (row) => row.issued_date ? new Date(row.issued_date).toLocaleDateString() : 'N/A'
                    },
                    {
                      key: 'expiry_date',
                      header: 'Expiry Date',
                      accessor: (row) => row.expiry_date ? new Date(row.expiry_date).toLocaleDateString() : 'N/A'
                    },
                    {
                      key: 'verification_status',
                      header: 'Verification Status',
                      accessor: (row) => {
                        // Handle both string and object verification_status
                        let status = 'Pending';
                        const verificationStatus = row.verification_status;
                        
                        if (!verificationStatus) {
                          status = 'Pending';
                        } else if (typeof verificationStatus === 'string') {
                          status = verificationStatus;
                        } else if (typeof verificationStatus === 'object') {
                          // If verification_status is an object, try to get the value property or name property
                          status = verificationStatus.value || verificationStatus.name || verificationStatus.verification_status || verificationStatus.status || 'Pending';
                        } else {
                          status = String(verificationStatus);
                        }
                        
                        // Ensure status is always a string
                        status = String(status || 'Pending');
                        
                        const variant = status === 'Approved' ? 'success' : status === 'Rejected' ? 'danger' : 'warning';
                        return <Badge bg={variant}>{status}</Badge>;
                      }
                    },
                    {
                      key: 'is_linked_document',
                      header: 'Linked',
                      accessor: (row) => row.is_linked_document ? 'Yes' : 'No'
                    }
                  ]}
                  data={staff.StaffDocuments || []}
                  actions={(row) => {
                    const actions = [
                      {
                        label: 'View',
                        icon: 'fas fa-eye',
                        onClick: () => navigate(`/staff/${id}/document/${row.id}`)
                      },
                      {
                        label: 'Download',
                        icon: 'fas fa-download',
                        onClick: () => handleDocumentDownload(row)
                      },
                    ];
                    if (canEditStaff) {
                      actions.push({
                        label: 'Edit',
                        icon: 'fas fa-edit',
                        onClick: () => navigate(`/staff/${id}/document/${row.id}/edit`)
                      });
                    }
                    if (canDeleteStaff) {
                      actions.push({
                        label: 'Delete',
                        icon: 'fas fa-trash',
                        className: 'text-danger',
                        onClick: () => handleDocumentDelete(row.id)
                      });
                    }
                    return actions;
                  }}
                  emptyMessage="No documents found"
                  emptyStateIcon="fas fa-file-alt"
                  getRowId={(row) => row.id}
                  onRowClick={(row, rowIndex, e) => {
                    // Don't navigate if clicking on action button or action dropdown
                    if (e.target.closest('.table-actions') || e.target.closest('.dropdown-menu') || e.target.closest('.dropdown-toggle')) {
                      return;
                    }
                    navigate(`/staff/${id}/document/${row.id}`);
                  }}
                />
                ) : (
                  <Row className="drive-grid">
                    {(staff.StaffDocuments || []).map((doc) => {
                      const fileIconType = getFileIcon(doc);
                      const iconColor = getFileIconColor(fileIconType);
                      const menuIcon = <FiMoreVertical size={18} />;
                      
                      return (
                        <Col key={doc.id} xs={6} sm={4} md={3} lg={2} xl={2} className="drive-item-col">
                          <Card 
                            className="drive-item file-item" 
                            onClick={(e) => {
                              const target = e.target;
                              if (target.closest('.drive-item-actions') || 
                                  target.closest('.dropdown') || 
                                  target.closest('.dropdown-toggle') ||
                                  target.closest('.dropdown-menu')) {
                                return;
                              }
                              navigate(`/staff/${id}/document/${doc.id}`);
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="drive-item-icon" style={{ color: iconColor }}>
                              {getFileIconComponent(doc, 48)}
                            </div>
                            <div 
                              className="drive-item-name" 
                              title={doc.document_title || doc.name || doc.file_name}
                            >
                              {doc.is_favorite && (
                                <span className="me-1" style={{ color: '#ffc107' }}>★</span>
                              )}
                              {doc.document_title || doc.name || doc.file_name}
                            </div>
                            {doc.expiry_date && (
                              <div className="text-muted small mt-auto" style={{ fontSize: '0.75rem', marginTop: 'auto' }}>
                                Expires: {new Date(doc.expiry_date).toLocaleDateString()}
                              </div>
                            )}
                            <div className="drive-item-actions" onClick={(e) => e.stopPropagation()}>
                              <Dropdown>
                                <Dropdown.Toggle 
                                  variant="link" 
                                  className="drive-menu-toggle-docs"
                                  id={`doc-dropdown-${doc.id}`}
                                >
                                  {menuIcon}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/staff/${id}/document/${doc.id}`);
                                    }}
                                  >
                                    <i className="fas fa-eye me-2"></i>
                                    View
                                  </Dropdown.Item>
                                  <Dropdown.Item 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDocumentDownload(doc);
                                    }}
                                  >
                                    <i className="fas fa-download me-2"></i>
                                    Download
                                  </Dropdown.Item>
                                  {canEditStaff ? (
                                  <Dropdown.Item 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/staff/${id}/document/${doc.id}/edit`);
                                    }}
                                  >
                                    <i className="fas fa-edit me-2"></i>
                                    Edit
                                  </Dropdown.Item>
                                  ) : null}
                                  {canDeleteStaff ? (
                                  <Dropdown.Item 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDocumentDelete(doc.id);
                                    }}
                                    className="text-danger"
                                  >
                                    <i className="fas fa-trash me-2"></i>
                                    Delete
                                  </Dropdown.Item>
                                  ) : null}
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                          </Card>
                        </Col>
                      );
                    })}
                    {(staff.StaffDocuments || []).length === 0 && (
                      <Col xs={12}>
                        <EmptyState
                          icon={<FaFileAlt />}
                          title="No Data Found"
                          description="Get started by adding a document"
                        />
                      </Col>
                    )}
                  </Row>
                )}
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Card.Body>
      </Card>

      <InternalFooter />
    </div>
  );
};

export default StaffDetails;
